import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, FormControlLabel, Switch, TextField, Button,
    FormControl, InputLabel, Select, MenuItem, Divider, Alert
} from '@mui/material';
import { PayrollService } from '../../services/payroll.service';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';
import { usePayrollInstitution } from '../../hooks/usePayrollInstitution';
import { IPayrollSettings, ISalaryComponent } from '../../interfaces/payroll.interface';
import { AccountingService } from '../../services/accounting.service';
import { AccountType, IAccount } from '../../interfaces/accounting.interface';
import { useDashboardAccess } from '../dashboard/useDashboardAccess';
import AccountSelect from '../accounting/AccountSelect';

type AccountIds = Pick<IPayrollSettings, 'salaryExpenseAccountId' | 'cashAccountId' | 'bankAccountId' | 'taxPayableAccountId'>;

const EMPTY_IDS: AccountIds = { salaryExpenseAccountId: null, cashAccountId: null, bankAccountId: null, taxPayableAccountId: null };

// Labels live under payroll:payrollSettings.accounts.<key>.
const ACCOUNT_FIELDS: { key: keyof AccountIds; types: AccountType[]; cashOrBank?: boolean }[] = [
    { key: 'salaryExpenseAccountId', types: ['EXPENSE'] },
    { key: 'cashAccountId', types: ['ASSET'], cashOrBank: true },
    { key: 'bankAccountId', types: ['ASSET'], cashOrBank: true },
    { key: 'taxPayableAccountId', types: ['LIABILITY'] },
];

// Earnings are costs; deductions are owed to someone (tax, PF) or recover an advance (an asset).
const COMPONENT_TYPES: Record<ISalaryComponent['type'], AccountType[]> = {
    EARNING: ['EXPENSE'],
    DEDUCTION: ['LIABILITY', 'ASSET', 'EXPENSE'],
};

const PayrollSettings: React.FC = () => {
    const { t } = useTranslation(['payroll', 'common']);
    const [deductForAbsence, setDeductForAbsence] = useState(false);
    const [workingDaysPerMonth, setWorkingDaysPerMonth] = useState(26);
    // null (never set) posts, matching the server, which only skips on an explicit false.
    const [autoPostJournalEntry, setAutoPostJournalEntry] = useState(true);
    const [loading, setLoading] = useState(false);
    const [accountIds, setAccountIds] = useState<AccountIds>(EMPTY_IDS);
    const [accounts, setAccounts] = useState<IAccount[]>([]);
    const [components, setComponents] = useState<ISalaryComponent[]>([]);
    const { showSuccess, showApiError } = useToast();
    const { isSystemAdmin, institutions, institutionId, setInstitutionId } = usePayrollInstitution();
    const { enabledModules, ready } = useDashboardAccess();
    // Operators provision the module but cannot read a customer's books, so they get no mapping UI.
    // Wait for `ready`: until the enabled-modules call returns, every module reads as enabled.
    const accountingOn = ready && enabledModules.has('accounting') && !isSystemAdmin;

    useEffect(() => {
        if (!institutionId) return;
        loadSettings();
    }, [institutionId]);

    useEffect(() => {
        if (!institutionId || !accountingOn) return;
        AccountingService.listAccounts(institutionId)
            .then(setAccounts)
            .catch(error => showApiError(error, t('payrollSettings.toast.loadAccountsFailed')));
        PayrollService.getSalaryComponents(institutionId)
            .then(setComponents)
            .catch(error => showApiError(error, t('shared.loadComponentsFailed')));
    }, [institutionId, accountingOn]); // eslint-disable-line react-hooks/exhaustive-deps

    const accountsOf = (types: AccountType[], cashOrBank?: boolean) =>
        accounts.filter(a => types.includes(a.type) && (!cashOrBank || a.cashOrBank));

    const mapComponent = async (component: ISalaryComponent, selected: string) => {
        const accountId = selected || null;
        try {
            await PayrollService.setSalaryComponentAccount(component.id, accountId);
            setComponents(prev => prev.map(c => (c.id === component.id ? { ...c, accountId } : c)));
            showSuccess(accountId
                ? t('payrollSettings.toast.mapped', { name: component.name })
                : t('payrollSettings.toast.unmapped', { name: component.name }));
        } catch (error) {
            showApiError(error, t('payrollSettings.toast.mapFailed'));
        }
    };

    const loadSettings = async () => {
        setLoading(true);
        try {
            const data: IPayrollSettings | null = await PayrollService.getPayrollSettings(institutionId);
            if (data) {
                setDeductForAbsence(!!data.deductForAbsence);
                setWorkingDaysPerMonth(data.workingDaysPerMonth ?? 26);
                setAutoPostJournalEntry(data.autoPostJournalEntry !== false);
                setAccountIds({
                    salaryExpenseAccountId: data.salaryExpenseAccountId ?? null,
                    cashAccountId: data.cashAccountId ?? null,
                    bankAccountId: data.bankAccountId ?? null,
                    taxPayableAccountId: data.taxPayableAccountId ?? null,
                });
            }
        } catch (error) {
            showApiError(error, t('payrollSettings.toast.loadFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            const payload: Partial<IPayrollSettings> = {
                deductForAbsence,
                workingDaysPerMonth,
                autoPostJournalEntry,
                // The server replaces all four ids on every save; send them back so none is wiped.
                ...accountIds,
            };
            await PayrollService.updatePayrollSettings(payload, institutionId);
            showSuccess(t('payrollSettings.toast.saved'));
        } catch (error) {
            showApiError(error, t('payrollSettings.toast.saveFailed'));
        }
    };

    if (loading) return <Box sx={{ p: 3 }}><Typography>{t('shared.loading')}</Typography></Box>;

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">{t('payrollSettings.title')}</Typography>
                {isSystemAdmin && (
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel>{t('shared.selectInstitution')}</InputLabel>
                        <Select
                            value={institutionId}
                            onChange={(e) => setInstitutionId(e.target.value)}
                            label={t('shared.selectInstitution')}
                        >
                            {institutions.map((inst) => (
                                <MenuItem key={inst.id} value={inst.id}>{inst.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
            </Box>

            <Paper sx={{ p: 3, maxWidth: 600 }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={deductForAbsence}
                            onChange={(e) => setDeductForAbsence(e.target.checked)}
                        />
                    }
                    label={t('payrollSettings.deductForAbsence')}
                    sx={{ mb: 3, display: 'block' }}
                />

                <TextField
                    label={t('payrollSettings.workingDays')}
                    type="number"
                    value={workingDaysPerMonth}
                    onChange={(e) => setWorkingDaysPerMonth(parseInt(e.target.value) || 26)}
                    helperText={t('payrollSettings.workingDaysHelper')}
                    fullWidth
                    sx={{ mb: 3 }}
                />

                {accountingOn && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle1">{t('payrollSettings.accounting')}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {t('payrollSettings.accountingHelp')}
                        </Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={autoPostJournalEntry}
                                    onChange={(e) => setAutoPostJournalEntry(e.target.checked)}
                                />
                            }
                            label={t('payrollSettings.autoPost')}
                            sx={{ mb: 2, display: 'block' }}
                        />
                        {ACCOUNT_FIELDS.map(f => (
                            <Box key={f.key} sx={{ mb: 2 }}>
                                <AccountSelect id={`payroll-${f.key}`} label={t(`payrollSettings.accounts.${f.key}`)}
                                               value={accountIds[f.key] ?? ''}
                                               accounts={accountsOf(f.types, f.cashOrBank)} emptyLabel={t('payrollSettings.default')}
                                               onChange={v => setAccountIds(prev => ({ ...prev, [f.key]: v || null }))} />
                            </Box>
                        ))}
                    </>
                )}

                <Button variant="contained" color="primary" onClick={handleSave}>
                    {t('payrollSettings.save')}
                </Button>
            </Paper>

            {accountingOn && components.length > 0 && (
                <Paper sx={{ p: 3, maxWidth: 600, mt: 3 }}>
                    <Typography variant="subtitle1">{t('payrollSettings.componentAccounts')}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {t('payrollSettings.componentAccountsHelp')}
                    </Typography>
                    {components.map(c => (
                        <Box key={c.id} sx={{ mb: 2 }}>
                            <AccountSelect id={`component-account-${c.id}`}
                                           label={t(c.type === 'EARNING' ? 'payrollSettings.accountForEarning' : 'payrollSettings.accountForDeduction', { name: c.name })}
                                           value={c.accountId ?? ''} accounts={accountsOf(COMPONENT_TYPES[c.type])}
                                           emptyLabel={t('payrollSettings.defaultByName')}
                                           onChange={v => mapComponent(c, v)} />
                        </Box>
                    ))}
                </Paper>
            )}
        </Box>
    );
};

export default PayrollSettings;
